import { RowValue } from '../ExecutiveSummaryGenericTable/types';
import { DevelopmentMetricId } from './metrics';
import { BlockResolver, RowDescriptor, SectionDescriptor } from './types';

export const DEVELOPMENT_COLUMNS = ['%', '$ / Built SF', '$ / Unit', 'Amount'] as const;

type Columns = typeof DEVELOPMENT_COLUMNS;
type DevelopmentRow = RowDescriptor<DevelopmentMetricId, Columns>;

export interface DevelopmentDynamicRow {
    id: string;
    label: string;
    cells: [RowValue, RowValue, RowValue, RowValue];
}

export interface DevelopmentUseGroup {
    id: string;
    title: string;
    rows: DevelopmentDynamicRow[];
}

export interface DevelopmentResolver extends BlockResolver<DevelopmentMetricId> {
    hasUses: () => boolean;
    hasSources: () => boolean;
    useGroups: () => DevelopmentUseGroup[];
    sources: () => DevelopmentDynamicRow[];
}

const totalRow = (id: string, label: string, rowKey: 'totalUses' | 'totalSources'): DevelopmentRow => ({
    id,
    label,
    cells: [
        { metric: `${rowKey}.percent` },
        { metric: `${rowKey}.perBuiltSf` },
        { metric: `${rowKey}.perUnit` },
        { metric: `${rowKey}.amount` },
    ],
});

const dataRow = (row: DevelopmentDynamicRow): DevelopmentRow => ({
    id: row.id,
    label: row.label,
    cells: [{ fixed: row.cells[0] }, { fixed: row.cells[1] }, { fixed: row.cells[2] }, { fixed: row.cells[3] }],
});

const totalSection = (id: string, row: DevelopmentRow): SectionDescriptor<DevelopmentMetricId, Columns> => ({
    id,
    columns: DEVELOPMENT_COLUMNS,
    headers: 'none',
    isTotal: true,
    rows: [row],
});

export const developmentLayout = (resolver: DevelopmentResolver): SectionDescriptor<DevelopmentMetricId, Columns>[] => {
    const sections: SectionDescriptor<DevelopmentMetricId, Columns>[] = [];

    if (resolver.hasUses()) {
        resolver.useGroups().forEach(group => sections.push({
            id: group.id,
            title: group.title,
            columns: DEVELOPMENT_COLUMNS,
            rows: group.rows.map(dataRow),
        }));
        sections.push(totalSection('uses-total', totalRow('total-uses', 'Total uses', 'totalUses')));
    }

    if (resolver.hasSources()) {
        sections.push({
            id: 'sources-section',
            title: 'Sources',
            columns: DEVELOPMENT_COLUMNS,
            headers: 'empty',
            rows: resolver.sources().map(dataRow),
        });
        sections.push(totalSection('sources-total', totalRow('total-sources', 'Total sources', 'totalSources')));
    }

    return sections;
};
